// useFeatureFlags — lightweight hook to check feature flags.
// Reads feature_flags table (RLS: authenticated read). Falls back to false
// when a flag is unknown, ensuring opt-in gating.
//
// Three rollout levels, any one of which turns a flag on:
//   enabled_globally   — the feature exists for everyone
//   enabled_for_orgs   — pilot for one org (every member of that org)
//   enabled_for_users  — pilot for ONE advisor, even inside a shared org
// The user pin exists so a feature can be tested on a single advisor without
// lighting it up for the rest of their org.

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/safeSupabase';
import { useAuth } from '@/hooks/useAuth';
import { useImpersonation } from '@/contexts/ImpersonationContext';

interface FeatureFlag {
  id: string;
  flag_name: string;
  enabled_globally: boolean;
  enabled_for_orgs: string[];
  enabled_for_users: string[];
}

const FLAG_COLUMNS = 'id, flag_name, enabled_globally, enabled_for_orgs, enabled_for_users';

async function fetchFlags(): Promise<FeatureFlag[]> {
  // `enabled_for_users` isn't in the auto-generated Supabase types yet (Drew
  // regenerates those); the cast keeps the typed client happy until then.
  const { data, error } = await supabase.from('feature_flags').select(FLAG_COLUMNS);
  if (error) throw error;
  return (data ?? []) as unknown as FeatureFlag[];
}

export function useFeatureFlags() {
  const { org, user } = useAuth();
  const { isImpersonating, impersonatedUserId } = useImpersonation();
  // Effective user, not session user: under View-as the session is still the
  // admin's, but a user pin must resolve for whoever the screen renders as.
  const effectiveUserId = (isImpersonating && impersonatedUserId) || user?.id;

  const { data: flags = [] } = useQuery({
    // CACHE KEY SCOPING — include org AND effective user ID so flags are
    // re-evaluated when switching accounts or impersonating. CLAUDE.md rule #9.
    queryKey: ['feature-flags', org?.id, effectiveUserId],
    queryFn: fetchFlags,
    staleTime: 60_000, // cache for 1 minute
  });

  /**
   * Returns true if the flag is enabled globally, for the current user's org,
   * or for the current user specifically. Always returns false for unknown flags.
   */
  const isEnabled = (flagName: string): boolean => {
    const flag = flags.find(f => f.flag_name === flagName);
    if (!flag) return false;
    if (flag.enabled_globally) return true;
    if (org?.id && flag.enabled_for_orgs.includes(org.id)) return true;
    if (effectiveUserId && (flag.enabled_for_users ?? []).includes(effectiveUserId)) return true;
    return false;
  };

  return { flags, isEnabled };
}
